import classNames from 'classnames';
import * as React from 'react';
import { RefObject } from 'react';

import { Bem } from '../../../service/Bem/Bem';
import { CampaignType, GroupType } from '../../../typings/domain';
import { HoverTooltipWrapper } from '../../HoverTooltipWrapper/HoverTooltipWrapper';
import { Loader } from '../../Loader/Loader';
import { TextInputDecorator } from '../../TextInputDecorator/TextInputDecorator';

import './Selector.css';
import { SelectorItem } from './SelectorItem/SelectorItem';

export enum SelectorItemsDisableReason {
    REACHED_COUNT_LIMIT = 'REACHED_COUNT_LIMIT',
    ARCHIVED = 'ARCHIVED'
}

export interface SelectorItems {
    id: string;
    name: string;
    type: CampaignType;
    isDisabled?: boolean;
    disableReason?: SelectorItemsDisableReason;
    minusKeywords?: string[];
    regionsIds?: number[];
    adsCount?: number;
    isAutoBudget?: boolean;
    groupType?: GroupType;
}

export interface SelectorProps {
    placeholder?: string;
    items?: Array<SelectorItems>;
    isLoading: boolean;
    disableTooltipText: {
        [SelectorItemsDisableReason.ARCHIVED]?: string;
        [SelectorItemsDisableReason.REACHED_COUNT_LIMIT]?: string;
    };
    onInputChange: (text: string) => void;
    onItemSelect: (selectorItems: SelectorItems) => void;
    itemSelected?: SelectorItems;
    inputText?: string;
    emptyText?: string;
    awaitText?: string;
    errorText?: string;
    isActive?: boolean;
    cls?: string;
}

const BLOCK = 'selector';

export class Selector extends React.PureComponent<SelectorProps> {
    private preselectRef: RefObject<HTMLDivElement> = React.createRef();

    componentDidUpdate() {
        if (this.preselectRef.current) {
            this.preselectRef.current.scrollIntoView();
        }
    }

    render() {
        const { inputText, placeholder, isActive, onInputChange, cls } = this.props;

        return (
            <div className={classNames(cls, Bem.cls(BLOCK))}>
                <div className={Bem.cls(BLOCK, 'input')}>
                    <TextInputDecorator
                        text={inputText}
                        placeholder={placeholder}
                        theme={'normal'}
                        view={'classic'}
                        size={'n'}
                        cls={Bem.cls(BLOCK, 'input')}
                        onChange={onInputChange}
                        disabled={!isActive}
                    />
                </div>
                <div className={Bem.cls('selector', 'list')}>{this.getListState()}</div>
            </div>
        );
    }

    private getListState = () => {
        const { isLoading, items, emptyText, isActive, errorText, awaitText, itemSelected, onItemSelect } = this.props;

        if (isLoading) {
            return <Loader size={'s'} />;
        }

        if (errorText) {
            return <div className={Bem.cls(BLOCK, 'text-info')}>{errorText}</div>;
        }

        if (!isActive) {
            return <div className={Bem.cls(BLOCK, 'text-info')}>{awaitText}</div>;
        }

        if (!items || !items.length) {
            return <div className={Bem.cls(BLOCK, 'text-info')}>{emptyText}</div>;
        } else {
            return items.map((item, index) => {
                return (
                    <div
                        ref={itemSelected && itemSelected.id === item.id ? this.preselectRef : undefined}
                        onClick={item.isDisabled ? undefined : () => onItemSelect(item)}
                        className={Bem.cls('selector', 'item-content', {
                            active: Boolean(itemSelected && item.id === itemSelected.id),
                            disabled: Boolean(item.isDisabled)
                        })}
                        key={index}
                    >
                        {this.renderItem(item)}
                    </div>
                );
            });
        }
    };

    private getHoverTooltipText(selectorItem: SelectorItems): string | undefined {
        const { disableTooltipText } = this.props;

        if (selectorItem.isDisabled && selectorItem.disableReason) {
            return disableTooltipText[selectorItem.disableReason];
        }

        return undefined;
    }

    private renderItem(selectorItem: SelectorItems) {
        const { itemSelected } = this.props;
        const isActive = itemSelected !== undefined && selectorItem.id === itemSelected.id;
        const tooltipText = this.getHoverTooltipText(selectorItem);

        if (!tooltipText) {
            return (
                <SelectorItem
                    isActive={isActive}
                    isDisabled={selectorItem.isDisabled === true}
                    name={selectorItem.name}
                    id={selectorItem.id}
                />
            );
        }

        return (
            <HoverTooltipWrapper
                tooltipText={tooltipText}
                to={['right', 'bottom']}
                isDoccenterTooltip={false}
                cls={Bem.cls(BLOCK, 'item')}
            >
                <SelectorItem
                    isActive={isActive}
                    isDisabled={selectorItem.isDisabled === true}
                    name={selectorItem.name}
                    id={selectorItem.id}
                />
            </HoverTooltipWrapper>
        );
    }
}
